// Selectors over the ledger. Cashflow, P&L, balance sheet — all derived here.
// Per ARCHITECTURE.md §3: nothing is ever stored separately from these fns' output.

import type { GameState, Txn, TxnCategory } from '../state/types.js';

const sumAmount = (txns: readonly Txn[]): number => txns.reduce((acc, t) => acc + t.amount, 0);

const filterWeek = (state: GameState, week: number): readonly Txn[] =>
  state.ledger.filter((t) => t.week === week);

const byCategory = (txns: readonly Txn[], category: TxnCategory): readonly Txn[] =>
  txns.filter((t) => t.category === category);

/**
 * Cashflow for a given week, per ECONOMY §1:
 *   cashflow = active + passive − expense − debt    (capital is excluded)
 * Returned as a signed number — positive = into wallet.
 */
export function cashflowForWeek(state: GameState, week: number): number {
  const txns = filterWeek(state, week);
  const active = sumAmount(byCategory(txns, 'active'));
  const passive = sumAmount(byCategory(txns, 'passive'));
  const expense = sumAmount(byCategory(txns, 'expense'));
  const debt = sumAmount(byCategory(txns, 'debt'));
  // expense + debt amounts are negative by convention, so a sum already gives the right sign.
  return active + passive + expense + debt;
}

/** Convenience — cashflow for the currently in-flight week. */
export function cashflowThisWeek(state: GameState): number {
  return cashflowForWeek(state, state.clock.week);
}

/**
 * Weekly passive income — the LHS of the level-win condition.
 * Sum of asset_yield + dividend + interest txns for the given week (or this week if omitted).
 */
export function passiveIncomeForWeek(state: GameState, week?: number): number {
  const w = week ?? state.clock.week;
  return state.ledger
    .filter((t) => t.week === w)
    .filter((t) => t.type === 'asset_yield' || t.type === 'dividend' || t.type === 'interest')
    .reduce((acc, t) => acc + t.amount, 0);
}

/**
 * Weekly living-expense burden — RHS of the win condition.
 * Sum of 'living' txns plus any active liability_upkeep this week.
 */
export function livingExpensesForWeek(state: GameState, week?: number): number {
  const w = week ?? state.clock.week;
  const livingAndUpkeep = state.ledger
    .filter((t) => t.week === w)
    .filter((t) => t.type === 'living' || t.type === 'liability_upkeep')
    .reduce((acc, t) => acc + Math.abs(t.amount), 0);
  return livingAndUpkeep;
}

/**
 * Win progress per ECONOMY: passive / living_expenses.
 * 0 means no progress, 1+ means financial freedom achieved.
 * Returns 0 when living=0 (defensive — won't divide by zero).
 */
export function winProgress(state: GameState): number {
  const living = livingExpensesForWeek(state);
  if (living <= 0) return 0;
  return passiveIncomeForWeek(state) / living;
}

// ----- Balance sheet -----

export type BalanceSheet = {
  cash: number;
  savings: number;
  assetsAtCost: number;
  liabilitiesAtCost: number;
  loanPrincipalRemaining: number;
  netWorth: number;
};

export function balanceSheet(state: GameState): BalanceSheet {
  const assetsAtCost = state.assets.reduce((a, x) => a + x.costPaid, 0);
  const liabilitiesAtCost = state.liabilities.reduce((a, x) => a + x.costPaid, 0);
  const loanPrincipalRemaining = state.loans.reduce((a, l) => a + l.remainingPrincipal, 0);
  const cash = state.wallet.cash;
  const savings = state.savings.balance;
  // Net worth = (cash + savings + asset value) − (liabilities at cost + outstanding loans).
  // Liabilities-at-cost approximates depreciation: simple for kids, honest enough for MVP.
  const netWorth = cash + savings + assetsAtCost - liabilitiesAtCost - loanPrincipalRemaining;
  return { cash, savings, assetsAtCost, liabilitiesAtCost, loanPrincipalRemaining, netWorth };
}

// ----- P&L for a week range -----

export type ProfitAndLoss = {
  fromWeek: number;
  toWeek: number;
  activeIncome: number; // revenue (sum of all 'active')
  passiveIncome: number;
  expenses: number; // positive number — gross outflow
  debtService: number; // positive number
  net: number; // active + passive − expenses − debtService
};

export function plForRange(state: GameState, fromWeek: number, toWeek: number): ProfitAndLoss {
  const inRange = state.ledger.filter((t) => t.week >= fromWeek && t.week <= toWeek);
  const active = sumAmount(byCategory(inRange, 'active'));
  const passive = sumAmount(byCategory(inRange, 'passive'));
  const expenses = -sumAmount(byCategory(inRange, 'expense')); // flip sign for readability
  const debtService = -sumAmount(byCategory(inRange, 'debt'));
  return {
    fromWeek,
    toWeek,
    activeIncome: active,
    passiveIncome: passive,
    expenses,
    debtService,
    net: active + passive - expenses - debtService,
  };
}
